import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  Chip,
  Alert,
  Divider,
  InputAdornment,
  Autocomplete,
  CircularProgress,
} from '@mui/material'
import { ServicePackageFormData } from '../../types/servicePackage'
import { getFieldError } from '../../utils/servicePackageValidation'
import { membershipApi } from '../../services/api'
import { useDebounce } from '../../hooks/useDebounce'
import api from '../../services/api'

interface Payer {
  _id: string
  payer_name: string
  payer_state: string
  payer_type: string
  workers_comp: string
  ub_claims: string
  eligibility: string
  auto: string
  attachment: string
  dent_claims: string
  era: string
  payer_alt_names: Array<{ _id: string; alt_payer_name: string }>
  avg_era_enroll_days: number
  secondary_support: string
  payerid: string
  '1500_claims': string
  status: string
  __v: number
  createdAt: string
  updatedAt: string
}

interface InsurancePayer {
  _id: string
  payer_name: string
}

interface Section3PricingBillingProps {
  formData: ServicePackageFormData
  onUpdate: (data: Partial<ServicePackageFormData>) => void
  errors: any
  initialInsurances?: InsurancePayer[] // Optional: insurance providers already selected in edit mode
}

export const Section3PricingBilling: React.FC<Section3PricingBillingProps> = ({
  formData,
  onUpdate,
  errors,
  initialInsurances,
}) => {
  // Convert initialInsurances to Payer[] format synchronously for initial state
  const getInitialPayers = React.useMemo(() => {
    if (initialInsurances && initialInsurances.length > 0) {
      return initialInsurances.map(insurance => ({
        _id: insurance._id,
        payer_name: insurance.payer_name,
        payer_state: '',
        payer_type: '',
        workers_comp: '',
        ub_claims: '',
        eligibility: '',
        auto: '',
        attachment: '',
        dent_claims: '',
        era: '',
        payer_alt_names: [],
        avg_era_enroll_days: 0,
        secondary_support: '',
        payerid: '',
        '1500_claims': '',
        status: '',
        __v: 0,
        createdAt: '',
        updatedAt: '',
      }))
    }
    return []
  }, [initialInsurances])

  // Payer search state - initialize from initialInsurances if available
  const [payerSearchInput, setPayerSearchInput] = useState('')
  // Initialize search options with initial payers immediately so chips show right away
  const [payerSearchOptions, setPayerSearchOptions] = useState<Payer[]>(
    () => getInitialPayers
  )
  const [isSearchingPayers, setIsSearchingPayers] = useState(false)
  const [isLoadingPayers, setIsLoadingPayers] = useState(false)
  const [isLoadingFromProfile, setIsLoadingFromProfile] = useState(false)
  const [selectedPayers, setSelectedPayers] = useState<Payer[]>(
    () => getInitialPayers
  )
  const [insuranceAcceptedIds, setInsuranceAcceptedIds] = useState<string[]>(
    () =>
      initialInsurances && initialInsurances.length > 0
        ? initialInsurances.map(i => i._id)
        : []
  )

  // Commission from therapist plan
  const [commissionPercentage, setCommissionPercentage] = useState<
    number | null
  >(null)
  const [providerFeeDiscountPercent, setProviderFeeDiscountPercent] = useState<
    number | null
  >(null)
  const [planName, setPlanName] = useState<string | null>(null)
  const [isLoadingCommission, setIsLoadingCommission] = useState(false)

  // Store insuranceAccepted from profile API
  const [profileInsuranceAccepted, setProfileInsuranceAccepted] = useState<
    Array<{
      _id: string
      payer_name: string
      payer_state?: string
      payerid?: string
    }>
  >([])
  const [hasLoadedProfileInsurance, setHasLoadedProfileInsurance] =
    useState(false)
  const hasPrefilledFromProfileRef = React.useRef(false)

  // Helper to prefill insurance from provider profile into this service package
  async function prefillInsuranceFromProfile() {
    // Require profile data and avoid double-prefill or overriding user changes
    if (
      !hasLoadedProfileInsurance ||
      profileInsuranceAccepted.length === 0 ||
      hasPrefilledFromProfileRef.current ||
      selectedPayers.length > 0
    ) {
      return
    }

    try {
      setIsLoadingPayers(true)
      setIsLoadingFromProfile(true)

      const profileInsuranceIds = profileInsuranceAccepted.map(i => i._id)

      // Try to load full payer objects that match the profile insurance
      let matchingPayers: Payer[] = []
      try {
        const payersRes = await membershipApi.getPayers()
        let allPayers: Payer[] = []
        if (Array.isArray(payersRes)) {
          allPayers = payersRes
        } else if (Array.isArray((payersRes as any)?.data)) {
          allPayers = (payersRes as any).data
        } else if (Array.isArray((payersRes as any)?.data?.data)) {
          allPayers = (payersRes as any).data.data
        }

        matchingPayers = allPayers.filter((p: Payer) =>
          profileInsuranceIds.includes(p._id)
        )
      } catch (e) {
        // Fallback: use searchPayers with empty string to get all payers
        const searchRes = await membershipApi.searchPayers('')
        let searchData: Payer[] = []
        if (Array.isArray(searchRes)) {
          searchData = searchRes
        } else if (Array.isArray((searchRes as any)?.data)) {
          searchData = (searchRes as any).data
        } else if (Array.isArray((searchRes as any)?.data?.data)) {
          searchData = (searchRes as any).data.data
        }
        matchingPayers = searchData.filter((p: Payer) =>
          profileInsuranceIds.includes(p._id)
        )
      }

      if (matchingPayers.length > 0) {
        // Use full payer objects from membership API
        setSelectedPayers(matchingPayers)

        const ids = matchingPayers.map(p => p._id)
        setInsuranceAcceptedIds(ids)

        // Ensure this service package accepts the same insurance as the profile
        onUpdate({
          pricingBilling: {
            ...formData.pricingBilling,
            acceptInsurance: true,
            acceptedPayers: ids,
          },
        })

        // Make sure these payers are available as options (for tags / future searches)
        setPayerSearchOptions(prevOptions => {
          const existingOptionIds = prevOptions.map(p => p._id)
          const newPayers = matchingPayers.filter(
            p => !existingOptionIds.includes(p._id)
          )
          return newPayers.length > 0
            ? [...prevOptions, ...newPayers]
            : prevOptions
        })
      } else {
        // If we can't resolve full payer objects, fall back to minimal data from profile
        const minimalPayers: Payer[] = profileInsuranceAccepted.map(
          insurance => ({
            _id: insurance._id,
            payer_name: insurance.payer_name || '',
            payer_state: insurance.payer_state || '',
            payer_type: '',
            workers_comp: '',
            ub_claims: '',
            eligibility: '',
            auto: '',
            attachment: '',
            dent_claims: '',
            era: '',
            payer_alt_names: [],
            avg_era_enroll_days: 0,
            secondary_support: '',
            payerid: insurance.payerid || '',
            '1500_claims': '',
            status: '',
            __v: 0,
            createdAt: '',
            updatedAt: '',
          })
        )

        setSelectedPayers(minimalPayers)
        setInsuranceAcceptedIds(profileInsuranceIds)

        onUpdate({
          pricingBilling: {
            ...formData.pricingBilling,
            acceptInsurance: true,
            acceptedPayers: profileInsuranceIds,
          },
        })

        setPayerSearchOptions(prevOptions => {
          const existingOptionIds = prevOptions.map(p => p._id)
          const newPayers = minimalPayers.filter(
            p => !existingOptionIds.includes(p._id)
          )
          return newPayers.length > 0
            ? [...prevOptions, ...newPayers]
            : prevOptions
        })
      }

      hasPrefilledFromProfileRef.current = true
    } catch (error) {
      console.error('Error pre-filling insurance from profile:', error)
    } finally {
      setIsLoadingPayers(false)
      setIsLoadingFromProfile(false)
    }
  }

  // Debounce search input
  const debouncedPayerSearch = useDebounce(payerSearchInput, 300)

  // Fetch therapist profile to get commission from plan
  useEffect(() => {
    const fetchCommission = async () => {
      setIsLoadingCommission(true)
      try {
        const response = await api.get('/therapist/profile')
        const data = response.data?.data || response.data
        const planData = data?.plan_id
        const membershipData = data?.membership_id

        if (planData) {
          const planNameValue = planData.name
          const subscriptionFrequency = membershipData?.subscription_frequency

          // Determine which commission to use based on subscription frequency
          let commissionToUse: number | null = null

          if (subscriptionFrequency === 'Month') {
            // User is on monthly plan
            // First try monthlyCommission from profile data, then fallback to plan_id.monthly_commission
            commissionToUse =
              data?.commission > 0
                ? data?.commission
                : (planData.commission ?? null)
          } else if (subscriptionFrequency === 'Year') {
            // User is on yearly plan
            // First try yearlyCommission from profile data, then fallback to plan_id.anual_commission
            commissionToUse =
              data?.commission > 0
                ? data?.commission
                : (planData.commission ?? null)
          } else {
            // Fallback: if subscription_frequency is not available, try to determine from plan commission
            const planCommission = planData.commission
            const monthlyCommission = planData.commission
            const anualCommission = planData.commission

            if (planCommission !== undefined && planCommission !== null) {
              if (
                monthlyCommission !== undefined &&
                planCommission === monthlyCommission
              ) {
                commissionToUse = data?.monthlyCommission ?? planCommission
              } else if (
                anualCommission !== undefined &&
                planCommission === anualCommission
              ) {
                commissionToUse = data?.commission ?? planCommission
              } else {
                commissionToUse = planCommission
              }
            }
          }

          if (commissionToUse !== null && commissionToUse !== undefined) {
            setCommissionPercentage(commissionToUse)
          }
          if (planNameValue) {
            setPlanName(planNameValue)
          }
        }

        // Fetch provider fee discount percentage
        const discountPercent = data?.providerFeeDiscountPercent
        if (discountPercent !== null && discountPercent !== undefined) {
          setProviderFeeDiscountPercent(discountPercent)
        } else {
          setProviderFeeDiscountPercent(null)
        }

        // Store insuranceAccepted from profile
        if (
          data?.insuranceAccepted &&
          Array.isArray(data.insuranceAccepted) &&
          data.insuranceAccepted.length > 0
        ) {
          // Handle both array of objects and array of IDs
          const insuranceData = data.insuranceAccepted.map((item: any) => {
            if (typeof item === 'string') {
              // If it's just an ID, return minimal object
              return {
                _id: item,
                payer_name: '',
                payer_state: '',
                payerid: '',
              }
            } else {
              // If it's an object, extract the fields we need
              return {
                _id: item._id || item.id,
                payer_name: item.payer_name || '',
                payer_state: item.payer_state || '',
                payerid: item.payerid || '',
              }
            }
          })
          setProfileInsuranceAccepted(insuranceData)
          setHasLoadedProfileInsurance(true)
        } else {
          setProfileInsuranceAccepted([])
          setHasLoadedProfileInsurance(true)
        }
      } catch (error) {
        console.error('Error fetching provider profile for commission:', error)
        // Fallback to formData commission if API fails
        setCommissionPercentage(formData.pricingBilling.commissionPercentage)
        setProviderFeeDiscountPercent(null)
        setHasLoadedProfileInsurance(true)
      } finally {
        setIsLoadingCommission(false)
      }
    }

    fetchCommission()
  }, []) // Only fetch once on mount

  // Auto-prefill insurance from profile when creating a NEW service package.
  // For edit mode (initialInsurances passed in, or acceptedPayers already set),
  // we skip this and rely on the existing initialization logic instead.
  useEffect(() => {
    if (
      !hasLoadedProfileInsurance ||
      profileInsuranceAccepted.length === 0 ||
      hasPrefilledFromProfileRef.current
    ) {
      return
    }

    // Treat as edit mode if we already have initialInsurances
    const isEditMode =
      (initialInsurances && initialInsurances.length > 0) ||
      (formData.pricingBilling.acceptedPayers &&
        formData.pricingBilling.acceptedPayers.length > 0)

    if (isEditMode) {
      return
    }

    // Only prefill if the user hasn't selected anything yet
    if (selectedPayers.length > 0) {
      return
    }

    // For a brand new package, prefill from profile so the provider
    // doesn't have to re-select insurance they already configured.
    prefillInsuranceFromProfile()
  }, [
    hasLoadedProfileInsurance,
    profileInsuranceAccepted,
    initialInsurances,
    selectedPayers,
    formData.pricingBilling.acceptedPayers,
  ])

  // Track if we've initialized from initialInsurances to prevent overwriting user changes
  const hasInitializedRef = React.useRef(false)
  const lastInitialInsurancesRef = React.useRef<InsurancePayer[] | undefined>(
    undefined
  )
  const hasFetchedPayerDetailsRef = React.useRef(false)

  // Immediately sync formData with initialInsurances on mount if available
  // This ensures chips show immediately without waiting for any async operations
  useEffect(() => {
    if (
      initialInsurances &&
      initialInsurances.length > 0 &&
      !hasInitializedRef.current
    ) {
      const initialPayerIds = initialInsurances.map(i => i._id)
      const currentAcceptedIds = formData.pricingBilling.acceptedPayers || []
      const idsMatch =
        initialPayerIds.length === currentAcceptedIds.length &&
        initialPayerIds.every(id => currentAcceptedIds.includes(id)) &&
        currentAcceptedIds.every(id => initialPayerIds.includes(id))

      if (!idsMatch) {
        // Update formData immediately to match initialInsurances
        onUpdate({
          pricingBilling: {
            ...formData.pricingBilling,
            acceptedPayers: initialPayerIds,
          },
        })
      }

      // Mark as initialized
      hasInitializedRef.current = true
      lastInitialInsurancesRef.current = initialInsurances
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run only once on mount to sync formData immediately

  // Pre-fetch full payer details when entering edit mode with initialInsurances
  // This eliminates loading time when user navigates to step 3
  // Runs in background without blocking the display of chips
  useEffect(() => {
    const preFetchPayerDetails = async () => {
      if (
        !initialInsurances ||
        initialInsurances.length === 0 ||
        hasFetchedPayerDetailsRef.current
      ) {
        return
      }

      const initialPayerIds = initialInsurances.map(i => i._id)

      // Check if we already have full payer details for all initial insurances
      const currentSelectedIds = selectedPayers.map(p => p._id)
      const hasAllDetails = initialPayerIds.every(
        id =>
          currentSelectedIds.includes(id) &&
          selectedPayers.find(p => p._id === id)?.payer_state !== undefined
      )

      if (hasAllDetails) {
        hasFetchedPayerDetailsRef.current = true
        return
      }

      // Don't set loading state - let chips show immediately with minimal data
      // Full details will be fetched in background and update silently
      // setIsLoadingPayers(true)
      try {
        // Fetch all payers to get full details
        const searchRes = await membershipApi.searchPayers('')
        const searchData = Array.isArray(searchRes)
          ? searchRes
          : searchRes?.data || searchRes?.data?.data || []

        // Filter to get only the initial payers with full details
        const foundPayers = searchData.filter((p: Payer) =>
          initialPayerIds.includes(p._id)
        )

        if (foundPayers.length > 0) {
          // Update selectedPayers with full payer details
          setSelectedPayers(prev => {
            const existingIds = prev.map(p => p._id)
            const newPayers = foundPayers.filter(
              p => !existingIds.includes(p._id)
            )
            // Replace existing minimal payer objects with full details
            const updatedPayers = prev.map(p => {
              const fullPayer = foundPayers.find(fp => fp._id === p._id)
              return fullPayer || p
            })
            return [...updatedPayers, ...newPayers]
          })

          // Add to search options
          setPayerSearchOptions(prevOptions => {
            const existingOptionIds = prevOptions.map(p => p._id)
            const newPayers = foundPayers.filter(
              p => !existingOptionIds.includes(p._id)
            )
            return newPayers.length > 0
              ? [...prevOptions, ...newPayers]
              : prevOptions
          })

          hasFetchedPayerDetailsRef.current = true
        }
      } catch (error) {
        console.error('Error pre-fetching payer details:', error)
        // Silently fail - chips are already showing with minimal data
      }
    }

    preFetchPayerDetails()
  }, [initialInsurances, selectedPayers])

  // Initialize from initialInsurances when it becomes available
  // This ensures formData is synced and initial payers are added to search options
  useEffect(() => {
    if (!initialInsurances || initialInsurances.length === 0) {
      return
    }

    const initialPayerIds = initialInsurances.map(i => i._id)
    const currentSelectedIds = selectedPayers.map(p => p._id)

    // Check if selectedPayers matches initialInsurances (from synchronous initialization)
    const matchesInitial =
      initialPayerIds.length === currentSelectedIds.length &&
      initialPayerIds.length > 0 &&
      initialPayerIds.every(id => currentSelectedIds.includes(id))

    // Check if we've already processed this initialInsurances
    const lastInitialIds = lastInitialInsurancesRef.current
      ? lastInitialInsurancesRef.current
        .map(i => i._id)
        .sort()
        .join(',')
      : ''
    const currentInitialIds = initialPayerIds.sort().join(',')
    const isSameInitialInsurances = lastInitialIds === currentInitialIds

    // Only initialize if:
    // 1. selectedPayers matches initialInsurances (from sync init), AND
    // 2. We haven't initialized yet, OR
    // 3. This is a different initialInsurances
    if (
      matchesInitial &&
      (!hasInitializedRef.current || !isSameInitialInsurances)
    ) {
      // Mark as initialized
      hasInitializedRef.current = true
      lastInitialInsurancesRef.current = initialInsurances

      // Ensure formData.acceptedPayers matches initialInsurances
      const currentAcceptedIds = formData.pricingBilling.acceptedPayers || []
      const idsMatch =
        initialPayerIds.length === currentAcceptedIds.length &&
        initialPayerIds.every(id => currentAcceptedIds.includes(id)) &&
        currentAcceptedIds.every(id => initialPayerIds.includes(id))

      if (!idsMatch) {
        // Update formData to match initialInsurances
        onUpdate({
          pricingBilling: {
            ...formData.pricingBilling,
            acceptedPayers: initialPayerIds,
          },
        })
      }

      // Add initial payers to search options
      setPayerSearchOptions(prevOptions => {
        const existingOptionIds = prevOptions.map(p => p._id)
        const newPayers = selectedPayers.filter(
          p => !existingOptionIds.includes(p._id)
        )
        return newPayers.length > 0
          ? [...prevOptions, ...newPayers]
          : prevOptions
      })
    }
  }, [initialInsurances, selectedPayers, formData.pricingBilling, onUpdate])

  // Load selected payers when component mounts or acceptedPayers changes
  // Similar to TherapistSettings - handles both array of IDs and array of objects
  // In edit mode, use initialInsurances if available to populate selectedPayers
  useEffect(() => {
    const loadSelectedPayers = async () => {
      const acceptedIds = formData.pricingBilling.acceptedPayers

      // Check if initialInsurances has changed or become available
      const initialInsurancesChanged =
        initialInsurances !== lastInitialInsurancesRef.current &&
        initialInsurances &&
        initialInsurances.length > 0

      // If we have initialInsurances (edit mode with full payer objects), use them directly
      // Initialize if:
      // 1. initialInsurances just became available/changed, OR
      // 2. We haven't initialized yet and have initialInsurances, OR
      // 3. We have initialInsurances but selectedPayers is empty (late initialization case)
      const hasInitialInsurances =
        initialInsurances && initialInsurances.length > 0
      const shouldInitialize =
        hasInitialInsurances &&
        (initialInsurancesChanged ||
          (!hasInitializedRef.current && hasInitialInsurances) ||
          (selectedPayers.length === 0 &&
            hasInitialInsurances &&
            !hasInitializedRef.current))

      if (shouldInitialize) {
        // Convert InsurancePayer[] to Payer[] format
        const initialPayers: Payer[] = initialInsurances.map(insurance => ({
          _id: insurance._id,
          payer_name: insurance.payer_name,
          payer_state: '',
          payer_type: '',
          workers_comp: '',
          ub_claims: '',
          eligibility: '',
          auto: '',
          attachment: '',
          dent_claims: '',
          era: '',
          payer_alt_names: [],
          avg_era_enroll_days: 0,
          secondary_support: '',
          payerid: '',
          '1500_claims': '',
          status: '',
          __v: 0,
          createdAt: '',
          updatedAt: '',
        }))

        const initialPayerIds = initialInsurances.map(i => i._id)

        // Check if user has already made changes (selected payers don't match initialInsurances)
        // If so, don't overwrite user's selection
        const currentSelectedIds = selectedPayers.map(p => p._id)
        const userHasMadeChanges =
          selectedPayers.length > 0 &&
          !initialPayerIds.every(id => currentSelectedIds.includes(id))

        // Only initialize if user hasn't made changes, or if selectedPayers is empty
        if (!userHasMadeChanges) {
          setSelectedPayers(initialPayers)
          setInsuranceAcceptedIds(initialPayerIds)
          hasInitializedRef.current = true
          lastInitialInsurancesRef.current = initialInsurances

          // Ensure formData.acceptedPayers matches initialInsurances if they don't match
          // This ensures consistency between the form data and the displayed selection
          const currentAcceptedIds =
            formData.pricingBilling.acceptedPayers || []
          const idsMatch =
            initialPayerIds.length === currentAcceptedIds.length &&
            initialPayerIds.every(id => currentAcceptedIds.includes(id)) &&
            currentAcceptedIds.every(id => initialPayerIds.includes(id))

          if (!idsMatch) {
            // Update formData to match initialInsurances
            onUpdate({
              pricingBilling: {
                ...formData.pricingBilling,
                acceptedPayers: initialPayerIds,
              },
            })
          }

          // Add initial payers to search options so they're available but filtered out
          setPayerSearchOptions(prevOptions => {
            const existingOptionIds = prevOptions.map(p => p._id)
            const newPayers = initialPayers.filter(
              p => !existingOptionIds.includes(p._id)
            )
            return newPayers.length > 0
              ? [...prevOptions, ...newPayers]
              : prevOptions
          })
          return
        } else {
          // User has made changes, just update the ref to track that we've seen this initialInsurances
          lastInitialInsurancesRef.current = initialInsurances
        }
      }

      // After initialization, sync with acceptedPayers but respect user changes
      // Only update if the acceptedPayers actually changed (not just a re-render)
      // Skip if we've already initialized and the user has made changes
      if (hasInitializedRef.current) {
        const currentSelectedIds = selectedPayers.map(p => p._id)
        const idsMatch =
          acceptedIds.length === currentSelectedIds.length &&
          acceptedIds.every(id => currentSelectedIds.includes(id)) &&
          currentSelectedIds.every(id => acceptedIds.includes(id))

        // If IDs match, no need to update (user changes are already reflected)
        if (idsMatch) {
          return
        }

        // If acceptedPayers is empty but we have selected payers, don't clear
        // (user might have just removed all, let onChange handle it)
        if (
          (!acceptedIds || acceptedIds.length === 0) &&
          selectedPayers.length > 0
        ) {
          return
        }
      }

      // If acceptedPayers is empty, clear selectedPayers
      if (!acceptedIds || acceptedIds.length === 0) {
        setSelectedPayers([])
        setInsuranceAcceptedIds([])
        return
      }

      // Store the IDs for syncing with search results
      setInsuranceAcceptedIds(acceptedIds)

      // Check current selected payers to see if we already have them
      setSelectedPayers(prevSelected => {
        const currentSelectedIds = prevSelected.map(p => p._id)

        // Filter out payers that are no longer in acceptedPayers
        const filteredSelected = prevSelected.filter(p =>
          acceptedIds.includes(p._id)
        )
        const filteredIds = filteredSelected.map(p => p._id)

        // Find missing IDs that need to be loaded
        const missingIds = acceptedIds.filter(id => !filteredIds.includes(id))

        // If we already have all payers, just return the filtered list
        if (missingIds.length === 0) {
          // Also ensure they're in search options
          setPayerSearchOptions(prevOptions => {
            const existingOptionIds = prevOptions.map(p => p._id)
            const newPayers = filteredSelected.filter(
              p => !existingOptionIds.includes(p._id)
            )
            return newPayers.length > 0
              ? [...prevOptions, ...newPayers]
              : prevOptions
          })
          return filteredSelected
        }

        // Fetch payer details for missing IDs
        // Skip if we're in edit mode and have already fetched payer details
        const fetchMissingPayers = async () => {
          // Check if we're in edit mode and have already fetched details
          // If missing IDs are part of initialInsurances, check if we have full details
          if (
            initialInsurances &&
            initialInsurances.length > 0 &&
            hasFetchedPayerDetailsRef.current
          ) {
            const initialPayerIds = initialInsurances.map(i => i._id)
            const missingAreInitial = missingIds.every(id =>
              initialPayerIds.includes(id)
            )

            if (missingAreInitial) {
              // Check if we already have full details for these payers
              // Check both filteredSelected (current filtered state) and prevSelected (original state)
              const hasFullDetails = missingIds.every(id => {
                const payer =
                  filteredSelected.find(p => p._id === id) ||
                  prevSelected.find(p => p._id === id)
                return payer && payer.payer_state !== undefined
              })

              if (hasFullDetails) {
                // Already have the data from pre-fetch, no need to fetch again
                return
              }
            }
          }

          setIsLoadingPayers(true)
          try {
            // Try to get all payers (empty string search returns all)
            const searchRes = await membershipApi.searchPayers('')
            const searchData = Array.isArray(searchRes)
              ? searchRes
              : searchRes?.data || searchRes?.data?.data || []

            // Filter to get only the missing payers
            const foundPayers = searchData.filter((p: Payer) =>
              missingIds.includes(p._id)
            )

            if (foundPayers.length > 0) {
              // Use functional update to ensure we don't lose any state changes
              setSelectedPayers(prev => {
                // Combine existing with new found payers, avoiding duplicates
                const existingIds = prev.map(p => p._id)
                const newPayers = foundPayers.filter(
                  p => !existingIds.includes(p._id)
                )
                return [...prev, ...newPayers]
              })
              // Also add to search options so they appear in dropdown
              setPayerSearchOptions(prevOptions => {
                const existingOptionIds = prevOptions.map(p => p._id)
                const newPayers = foundPayers.filter(
                  p => !existingOptionIds.includes(p._id)
                )
                return newPayers.length > 0
                  ? [...prevOptions, ...newPayers]
                  : prevOptions
              })
            }
          } catch (error) {
            console.error('Error loading payer details:', error)
          } finally {
            setIsLoadingPayers(false)
          }
        }

        fetchMissingPayers()
        return filteredSelected
      })
    }

    loadSelectedPayers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.pricingBilling.acceptedPayers, initialInsurances])

  // Separate effect to handle initialInsurances initialization when it becomes available
  // This ensures that even if initialInsurances arrives late, we still initialize
  useEffect(() => {
    if (!initialInsurances || initialInsurances.length === 0) {
      return
    }

    // Check if we've already initialized from this initialInsurances
    const currentInitialIds = initialInsurances
      .map(i => i._id)
      .sort()
      .join(',')
    const lastInitialIds = lastInitialInsurancesRef.current
      ? lastInitialInsurancesRef.current
        .map(i => i._id)
        .sort()
        .join(',')
      : ''

    // If this is the same initialInsurances we've already processed, skip
    if (currentInitialIds === lastInitialIds && hasInitializedRef.current) {
      return
    }

    // Check current selectedPayers to see if we need to initialize
    const currentSelectedIds = selectedPayers
      .map(p => p._id)
      .sort()
      .join(',')
    const matchesInitial =
      currentInitialIds === currentSelectedIds && selectedPayers.length > 0

    // Only initialize if:
    // 1. selectedPayers is empty but we have initialInsurances (late initialization), OR
    // 2. We haven't initialized yet and selectedPayers doesn't match initialInsurances
    const isNewInitialInsurances = currentInitialIds !== lastInitialIds
    const shouldInitialize =
      (selectedPayers.length === 0 && initialInsurances.length > 0) ||
      (!hasInitializedRef.current &&
        initialInsurances.length > 0 &&
        !matchesInitial) ||
      (isNewInitialInsurances && selectedPayers.length === 0)

    if (!shouldInitialize) {
      // If selectedPayers already matches initialInsurances, just ensure formData is synced
      if (matchesInitial && !hasInitializedRef.current) {
        hasInitializedRef.current = true
        lastInitialInsurancesRef.current = initialInsurances

        const currentAcceptedIds = formData.pricingBilling.acceptedPayers || []
        const initialPayerIds = initialInsurances.map(i => i._id)
        const idsMatch =
          initialPayerIds.length === currentAcceptedIds.length &&
          initialPayerIds.every(id => currentAcceptedIds.includes(id)) &&
          currentAcceptedIds.every(id => initialPayerIds.includes(id))

        if (!idsMatch) {
          onUpdate({
            pricingBilling: {
              ...formData.pricingBilling,
              acceptedPayers: initialPayerIds,
            },
          })
        }

        // Add to search options
        setPayerSearchOptions(prevOptions => {
          const existingOptionIds = prevOptions.map(p => p._id)
          const newPayers = selectedPayers.filter(
            p => !existingOptionIds.includes(p._id)
          )
          return newPayers.length > 0
            ? [...prevOptions, ...newPayers]
            : prevOptions
        })
      }
      return
    }

    // Convert InsurancePayer[] to Payer[] format
    const initialPayers: Payer[] = initialInsurances.map(insurance => ({
      _id: insurance._id,
      payer_name: insurance.payer_name,
      payer_state: '',
      payer_type: '',
      workers_comp: '',
      ub_claims: '',
      eligibility: '',
      auto: '',
      attachment: '',
      dent_claims: '',
      era: '',
      payer_alt_names: [],
      avg_era_enroll_days: 0,
      secondary_support: '',
      payerid: '',
      '1500_claims': '',
      status: '',
      __v: 0,
      createdAt: '',
      updatedAt: '',
    }))

    const initialPayerIds = initialInsurances.map(i => i._id)

    if (shouldInitialize) {
      // Update refs and state
      hasInitializedRef.current = true
      lastInitialInsurancesRef.current = initialInsurances

      setSelectedPayers(initialPayers)
      setInsuranceAcceptedIds(initialPayerIds)

      // Update formData to match initialInsurances
      onUpdate({
        pricingBilling: {
          ...formData.pricingBilling,
          acceptedPayers: initialPayerIds,
        },
      })

      // Add initial payers to search options
      setPayerSearchOptions(prevOptions => {
        const existingOptionIds = prevOptions.map(p => p._id)
        const newPayers = initialPayers.filter(
          p => !existingOptionIds.includes(p._id)
        )
        return newPayers.length > 0
          ? [...prevOptions, ...newPayers]
          : prevOptions
      })
    }
  }, [initialInsurances, selectedPayers, formData.pricingBilling, onUpdate])

  // Search payers when debounced input changes
  useEffect(() => {
    const searchPayers = async () => {
      if (debouncedPayerSearch.trim().length === 0) {
        // When there's no search term, don't show any dropdown options,
        // but keep any already selected payers as chips.
        setPayerSearchOptions([])
        setIsSearchingPayers(false)
        return
      }

      setIsSearchingPayers(true)

      try {
        const response = await membershipApi.searchPayers(debouncedPayerSearch)
        // Normalize API response shape (handles array, { data: [] }, or { data: { data: [] } })
        let payersData: Payer[] = []
        if (Array.isArray(response)) {
          payersData = response
        } else if (Array.isArray((response as any)?.data)) {
          payersData = (response as any).data
        } else if (Array.isArray((response as any)?.data?.data)) {
          payersData = (response as any).data.data
        }

        if (payersData.length > 0) {
          // Get IDs of already selected payers to filter them out
          const selectedPayerIds = selectedPayers.map(p => p._id)

          // Filter out already selected payers from search results
          const filteredPayers = payersData.filter(
            p => !selectedPayerIds.includes(p._id)
          )

          setPayerSearchOptions(filteredPayers)
        } else {
          // No results for this search term
          setPayerSearchOptions([])
        }
      } catch (error) {
        console.error('Error searching payers:', error)
      } finally {
        setIsSearchingPayers(false)
      }
    }

    searchPayers()
  }, [debouncedPayerSearch, selectedPayers])

  // Update selectedPayers when search options include previously selected IDs
  // This ensures that when we search and find payers that match our insuranceAccepted IDs,
  // they get added to selectedPayers
  useEffect(() => {
    if (payerSearchOptions.length > 0 && insuranceAcceptedIds.length > 0) {
      setSelectedPayers(prevSelected => {
        const currentSelectedIds = prevSelected.map(p => p._id)
        const missingIds = insuranceAcceptedIds.filter(
          id => !currentSelectedIds.includes(id)
        )

        if (missingIds.length === 0) return prevSelected

        const newPayers = missingIds
          .map(id => payerSearchOptions.find(p => p._id === id))
          .filter((p): p is Payer => p !== undefined)

        // Also add new payers to search options if not already there
        if (newPayers.length > 0) {
          setPayerSearchOptions(prev => {
            const existingOptionIds = prev.map(p => p._id)
            const additionalPayers = newPayers.filter(
              p => !existingOptionIds.includes(p._id)
            )
            return additionalPayers.length > 0
              ? [...prev, ...additionalPayers]
              : prev
          })
        }

        return [...prevSelected, ...newPayers]
      })
    }
  }, [payerSearchOptions, insuranceAcceptedIds])

  // Handle insurance selection change
  const handleInsuranceAcceptedChange = (
    event: React.SyntheticEvent,
    newValue: Payer[]
  ) => {
    // Update local state immediately
    setSelectedPayers(newValue)
    const newIds = newValue.map(payer => payer._id)
    setInsuranceAcceptedIds(newIds)

    // Mark as initialized so useEffect doesn't overwrite
    hasInitializedRef.current = true

    // Update form data
    onUpdate({
      pricingBilling: {
        ...formData.pricingBilling,
        acceptedPayers: newIds,
      },
    })
    // Clear input after selection
    setTimeout(() => {
      setPayerSearchInput('')
    }, 0)
  }

  const privatePayRateError = getFieldError(errors, 'privatePayRate')
  const acceptedPayersError = getFieldError(errors, 'acceptedPayers')

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Pricing & Billing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Set your pricing structure and billing preferences for this service
        package.
      </Typography>

      {/* Pricing Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Pricing Structure
          </Typography>

          <Grid container spacing={3}>
            {/* Private Pay Rate */}
            <Grid item xs={12}>
              <TextField
                id="private-pay-rate-field"
                fullWidth
                label="Private Pay Rate"
                type="number"
                value={formData.pricingBilling.privatePayRate || ''}
                onChange={e => {
                  const inputValue = e.target.value
                  // Allow empty input for clearing the field
                  if (inputValue === '') {
                    onUpdate({
                      pricingBilling: {
                        ...formData.pricingBilling,
                        privatePayRate: null,
                      },
                    })
                    return
                  }
                  // Parse the value
                  const numValue = parseFloat(inputValue)
                  // Reject if not a valid number, negative, or zero
                  if (isNaN(numValue) || numValue <= 0) {
                    return // Don't update if invalid
                  }
                  // Update with valid positive value
                  onUpdate({
                    pricingBilling: {
                      ...formData.pricingBilling,
                      privatePayRate: numValue,
                    },
                  })
                }}
                onBlur={e => {
                  // On blur, ensure value is valid or clear it
                  const numValue = parseFloat(e.target.value)
                  if (isNaN(numValue) || numValue <= 0) {
                    onUpdate({
                      pricingBilling: {
                        ...formData.pricingBilling,
                        privatePayRate: null,
                      },
                    })
                  }
                }}
                error={!!privatePayRateError}
                helperText={
                  privatePayRateError ||
                  'Rate per session for private pay patients (must be greater than 0)'
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0.01, step: 0.01 }}
                required={true}
              />
            </Grid>
          </Grid>

          {/* Commission Display */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {isLoadingCommission ? (
                <>
                  <strong>Per-Claim System Fee:</strong>{' '}
                  <CircularProgress
                    size={14}
                    sx={{ ml: 1, verticalAlign: 'middle' }}
                  />
                </>
              ) : (
                (() => {
                  const baseCommission =
                    commissionPercentage ??
                    formData.pricingBilling.commissionPercentage ??
                    0
                  const discount = providerFeeDiscountPercent ?? 0
                  const finalCommission = Math.max(0, baseCommission - discount)

                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <strong>Per-Claim System Fee:</strong>{' '}
                      {discount > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <span
                            style={{
                              textDecoration: 'line-through',
                              opacity: 0.6,
                              marginRight: '4px',
                              marginLeft: '8px',
                            }}
                          >
                            {baseCommission.toFixed(2)}%
                          </span>
                          <span
                            style={{
                              color: '#d32f2f',
                              fontWeight: 500,
                              marginRight: '4px',
                            }}
                          >
                            -{discount.toFixed(2)}%
                          </span>
                          {' = '}
                          <span
                            style={{
                              fontWeight: 600,
                              color: '#2e7d32',
                              marginLeft: '4px',
                            }}
                          >
                            {finalCommission.toFixed(2)}%
                          </span>
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              ml: 1,
                              color: 'text.secondary',
                              fontSize: '0.75rem',
                              display: 'block',
                              mt: 0.5,
                            }}
                          >
                            (Base fee: {baseCommission.toFixed(2)}% - Provider
                            discount: {discount.toFixed(2)}% = Final fee:{' '}
                            {finalCommission.toFixed(2)}%)
                          </Typography>
                        </Box>
                      ) : (
                        <span style={{ fontWeight: 600, marginLeft: '4px' }}>
                          {baseCommission}%
                        </span>
                      )}
                    </Box>
                  )
                })()
              )}
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This fee is collected via Stripe Connect as an application fee
                  for PillarThera’s AI-driven claim automation, payer-rule
                  intelligence, and revenue-cycle infrastructure.
                </Typography>
              </Alert>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Removed Session Includes Section */}

      {/* Insurance Section */}
      <Card>
        <CardContent id="pricing-billing-insurance-section">
          <Typography variant="h6" gutterBottom>
            Insurance Acceptance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose whether to accept insurance for this service package.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.pricingBilling.acceptInsurance}
                onChange={async e => {
                  const isEnabled = e.target.checked

                  // Update the toggle state
                  onUpdate({
                    pricingBilling: {
                      ...formData.pricingBilling,
                      acceptInsurance: isEnabled,
                      acceptedPayers: isEnabled
                        ? formData.pricingBilling.acceptedPayers
                        : [],
                    },
                  })

                  if (isEnabled) {
                    // When enabling, allow prefill from profile (new or existing package)
                    hasPrefilledFromProfileRef.current = false
                    await prefillInsuranceFromProfile()
                  } else {
                    // When disabling, clear the "has prefilled" flag so that if the
                    // user re-enables insurance we can prefill again if appropriate.
                    hasPrefilledFromProfileRef.current = false
                  }
                }}
                color="primary"
              />
            }
            label="Accept Insurance"
            sx={{ mb: 2 }}
          />

          {formData.pricingBilling.acceptInsurance && (
            <Box>
              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Accepted Payers
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Search and select the insurance payers you accept for this
                service package.
              </Typography>

              {acceptedPayersError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {acceptedPayersError}
                </Alert>
              )}

              {/* Payer Search Autocomplete */}
              <Box sx={{ mb: 2 }} id="accepted-payers-section">
                <Autocomplete
                  multiple
                  options={payerSearchOptions}
                  value={selectedPayers}
                  inputValue={payerSearchInput}
                  onChange={handleInsuranceAcceptedChange}
                  onInputChange={(event, newInputValue, reason) => {
                    // Only update input when user is typing, not when clearing/resetting
                    if (reason === 'input') {
                      setPayerSearchInput(newInputValue)
                    } else if (reason === 'clear') {
                      setPayerSearchInput('')
                    }
                  }}
                  getOptionLabel={option => option.payer_name}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  loading={isSearchingPayers || isLoadingPayers}
                  disabled={isLoadingFromProfile}
                  filterOptions={x => x} // Disable client-side filtering since we're using server-side search
                  noOptionsText={
                    payerSearchInput.trim().length === 0
                      ? 'Start typing to search for insurance providers'
                      : 'No insurance providers found'
                  }
                  onClose={(event, reason) => {
                    // Clear input when an option is selected
                    if (reason === 'selectOption') {
                      setPayerSearchInput('')
                    }
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      disabled={isLoadingFromProfile}
                      placeholder={
                        isLoadingFromProfile
                          ? 'Loading insurance from account settings...'
                          : 'Search and select insurance providers'
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: isLoadingFromProfile ? 'grey.50' : 'white',
                          '& fieldset': {
                            borderColor: 'grey.300',
                          },
                          '&:hover fieldset': {
                            borderColor: isLoadingFromProfile
                              ? 'grey.300'
                              : 'grey.400',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2E7DAF',
                            borderWidth: 2,
                          },
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isSearchingPayers || isLoadingPayers ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option._id}
                        label={option.payer_name}
                        size="small"
                        sx={{
                          bgcolor: '#E3F2FD',
                          color: '#2E7DAF',
                          '& .MuiChip-deleteIcon': {
                            color: '#2E7DAF',
                          },
                        }}
                      />
                    ))
                  }
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Billing Information Alerts */}
      <Box sx={{ mt: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Insurance Billing:</strong> Insurance payments are issued by
            the payer following claim submission and approval and are deposited
            directly to the Provider's account.
          </Typography>
        </Alert>

        <Alert severity="info">
          <Typography variant="body2">
            <strong>Private Pay:</strong> Private-pay services are processed
            through Stripe Connect, with payments deposited directly into the
            Provider's Stripe account.
          </Typography>
        </Alert>
      </Box>
    </Box>
  )
}
